/// <reference types="user-agent-data-types" />

import Cookie from 'js-cookie';

type ObserverCallbacks = {
	onAdded?: (node: Node) => void;
	onChanged?: (node: MutationRecord) => void;
	onRemoved?: (node: Node) => void;
};

/**
 * Appends a script tag to the markup
 * @param scriptSrc - the script source URL
 * @param attributes - optional attributes to add to the script tag
 */
export function appendScript(
	scriptSrc: string,
	attributes: Partial<Omit<HTMLScriptElement, 'src'>> = {},
) {
	if (!isValidUrl(scriptSrc)) {
		throw Error('Invalid script URL provided');
	}

	const firstScript = document.getElementsByTagName('script')[0];

	if (!firstScript?.parentNode) {
		throw Error('No parent node found in document');
	}

	const scriptTag = document.createElement('script');

	scriptTag.src = scriptSrc;

	const entries = Object.entries(attributes);

	if (entries.length) {
		for (const [key, value] of entries) {
			scriptTag.setAttribute(key, String(value));
		}
	}

	firstScript.parentNode.insertBefore(scriptTag, firstScript);
}

/**
 * Returns whether the GDPR consent has been given
 * @returns {boolean}
 */
export function consentGiven(): boolean {
	return Cookie.get('DSGVO_ZUSAGE_V1') === 'true' || false;
}

/**
 * Helper function to deal with the Mutation Observer API.
 * @param container - the container to observe for mutations
 * @param callbacks - callback functions to be called on mutation events
 * @param observerOptions - options passed to `observer.observe()` {@see @link https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/observe#parameters}
 * @returns
 */
export function createMutationObserver(
	callbacks: ObserverCallbacks,
	observerOptions: MutationObserverInit = {
		attributeOldValue: true,
		attributes: true,
		childList: true,
		subtree: true,
	},
) {
	const observer = new MutationObserver((mutationList) => {
		for (const mutation of mutationList) {
			if (mutation.type === 'childList') {
				if (typeof callbacks?.onAdded === 'function') {
					mutation.addedNodes.forEach(callbacks.onAdded);
				}

				if (typeof callbacks?.onRemoved === 'function') {
					mutation.removedNodes.forEach(callbacks.onRemoved);
				}
			}

			if (typeof callbacks?.onChanged === 'function' && mutation.type === 'attributes') {
				callbacks.onChanged(mutation);
			}
		}
	});

	return {
		disconnect: () => observer.disconnect(),
		observe: (container: Node) => observer.observe(container, observerOptions),
	};
}

/**
 * Determine whether a GUID is within a certain percentage of a segment
 * @param guid - the GUID to check
 * @param percentage - the percentage threshold (0-100)
 * @returns true if the GUID is within the segment, false otherwise
 */
export function isGuidWithinSegment(guid: string, percentage: number): boolean {
	if (typeof guid !== 'string') {
		throw new TypeError('The provided GUID argument is not a string.');
	}

	if (guid.length < 2) {
		throw new TypeError('The provided GUID needs to be at least two characters long.');
	}

	if (typeof percentage !== 'number') {
		throw new TypeError('The provided percentage argument is not a number.');
	}

	// Define a threshold for a GUID (Globally Unique Identifier) based on a percentage.
	// The 0x100 represents 256 in hexadecimal, which is the maximum value a byte can hold.
	const guidThreshold = (0x100 / 100) * percentage;

	// Extract the last two characters from the GUID string.
	// Since a byte in a GUID is represented by two hexadecimal digits, this effectively gets the last byte of the GUID.
	const lastByte = stringToHex(guid).slice(-2);

	// Convert the last byte from a hexadecimal string to an integer.
	// This is useful for numerical comparisons and operations.
	const lastByteInt = Number.parseInt(lastByte, 16);

	return lastByteInt < guidThreshold;
}

/**
 * Returns whether a script-tag is in the document
 * @param scriptSrc - the script source URL
 */
export function hasScript(scriptSrc: string): boolean {
	return Boolean(document.querySelectorAll(`[src^="${scriptSrc}"]`).length);
}

/**
 * Tests whether a string is a valid URL
 * @param url - the string to test
 * @returns true if the string is a valid URL, false otherwise
 */
export function isValidUrl(url: string): boolean {
	try {
		return Boolean(new URL(url));
	} catch {
		return false;
	}
}

/**
 * Sends a message element that triggers the Snackbar component to display a message.
 * @param message - message string to be shown in snackbar
 */
export function showSnackbarMessage(message: string) {
	sendMessage('snackbar:message', { message });
}

/**
 * Dispatches a postMessage on the window.
 * @param message - the message name
 * @param payload - the payload to send with the message; currently preliminarily typed
 */
export function sendMessage(message: string, payload?: { [key: string]: unknown }): void {
	window.postMessage(
		{
			eventName: message,
			payload,
		},
		document.location.href,
	);
}

/**
 * Encodes a string to hexadecimal if it contains non-hexadecimal characters
 * @param input
 */
export function stringToHex(input: string) {
	return /[^a-fA-F0-9]/.test(input)
		? [...input].map((char) => char.charCodeAt(0).toString(16)).join('')
		: input;
}
/**
 * Returns a normalized platform string.
 */
export function getPlatform() {
	if (navigator.userAgentData) {
		return normalizePlatform(navigator.userAgentData.platform);
	}

	if ('platform' in navigator) {
		return normalizePlatform(navigator.platform);
	}

	// @ts-expect-error
	return normalizePlatform(navigator.userAgent);
}

/**
 * Normalizes the platform string.
 * @param platform string that contains a platform name
 * @returns a normalized platform string
 */
function normalizePlatform(
	platform: string,
): 'android' | 'ios' | 'mac' | 'windows' | 'linux' | 'other' {
	const lowercasePlatform = platform.toLowerCase();

	switch (true) {
		case lowercasePlatform.includes('ios'):
		case lowercasePlatform.includes('ipad'):
		case lowercasePlatform.includes('iphone'):
			return 'ios';

		case lowercasePlatform.includes('android'):
			return 'android';

		case lowercasePlatform.includes('windows'):
		case lowercasePlatform.includes('win32'):
			return 'windows';

		case lowercasePlatform.includes('mac os x'):
		case lowercasePlatform.includes('macintel'):
		case lowercasePlatform.includes('macintosh'):
		case lowercasePlatform.includes('macos'):
			return 'mac';

		case lowercasePlatform.includes('chrome os'):
		case lowercasePlatform.includes('linux'):
			return 'linux';

		default:
			return 'other';
	}
}
